import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError } from "@/shared/api/errors";
import { useSaveOnboardingFlow } from "@/shared/api/onboarding";
import { problem } from "../blocks";
import { toBlocks, toSteps, type DraftStep } from "../steps";

import type { OnboardingFlow } from "@/shared/types";

/* The flow being edited: the saved one until the owner changes something, then a draft
   until Save sends the whole of it (`PUT /agent_onboarding_flows/{id}`) or Discard drops
   it. It applies at once — the flow isn't part of a version. A refused save keeps the
   draft and its error until the next edit. Also which step is open: the first, until the
   owner picks another. */
export function useFlowDraft(flow: OnboardingFlow, agentName: string) {
  const saved = useMemo(() => toSteps(flow.blocks), [flow]);
  const [draft, setDraft] = useState<DraftStep[] | null>(null);
  // undefined: the first step; null: none (the open one was removed)
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const save = useSaveOnboardingFlow(flow.id);
  const steps = draft ?? saved;
  const selected = picked === undefined ? steps[0]?.uid ?? null : picked;

  const edit = (next: DraftStep[]) => {
    setDraft(next);
    if (save.isError) save.reset();
  };

  return {
    steps,
    dirty: draft !== null,
    issue: problem(steps),
    selected,
    select: (uid: string | null) => setPicked(uid),
    edit,
    /** Change one step, by its uid. */
    change: (uid: string, fn: (step: DraftStep) => DraftStep) => edit(steps.map((s) => (s.uid === uid ? fn(s) : s))),
    remove: (uid: string) => {
      edit(steps.filter((s) => s.uid !== uid));
      if (uid === selected) setPicked(undefined);
    },
    discard: () => {
      setDraft(null);
      setPicked(undefined);
      save.reset();
    },
    save: () => {
      const at = steps.findIndex((s) => s.uid === selected);
      save.mutate({ blocks: toBlocks(steps) }, {
        onSuccess: (next) => {
          setDraft(null);
          // the open step stays open under the id the server gave it
          setPicked(at >= 0 ? next.blocks[at]?.id ?? undefined : undefined);
          toast.success("Onboarding saved", { description: `People meet the new flow the next time they come to ${agentName}.` });
        },
      });
    },
    saving: save.isPending,
    refused: save.isError ? (save.error instanceof ApiError ? save.error : new ApiError(save.error.message, 0)) : null,
  };
}

export type FlowDraftState = ReturnType<typeof useFlowDraft>;
